import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEvent } from "../context/EventProvider";
import { sameSession } from "../auth/session";
import { useSessionKey } from "../auth/useSessionKey";
import { FieldRow } from "../funnel/Champ";
import {
  isEmail,
  emailRefusal,
  allInvalid,
  type Complaint,
} from "../funnel/checks";
import { fetchAnswers, saveAnswers, valueOf, lockedOf } from "../funnel/forms";
import { visibleFields } from "../funnel/conditions";
import { useChosenForm, useWords } from "../funnel/useForm";
import { useParticipation } from "../funnel/useParticipation";
import { useSitePages } from "../pages/nav";
import { useMode } from "../panel/mode";
import { FORM_PAGE, formIdKey, htmlKey, pageOfInstance } from "../pages/repeatable";
import { withContext } from "./registry";
import { useT, useText } from "../settings/context";
import { Button } from "../ui/Button";
import { Prose } from "../ui/Prose";

/**
 * ═══ UNE PAGE FORMULAIRE : LE FORMULAIRE DE L'ORGANISATEUR, HABILLÉ PAR LE PRODUIT ═══
 *
 * « Comme les sections, on peut ajouter des pages qui comportent un formulaire que
 * l'on configure » (2026-08-20). On peut en poser autant qu'on veut sur l'accueil :
 * cet écran les sert TOUTES. La première répond à `/form` avec les clés nues ; chaque
 * exemplaire ajouté répond à `/form/<n>` avec les clés suffixées
 * (`pages/repeatable.ts`), exactement comme la section et comme l'étape répétable.
 *
 * ⚠️ CET ÉCRAN REMPLACE `Satisfaction`, dont il est la généralisation. Cette page-là
 * faisait déjà tout cela sous un nom fixe et un seul jeu de clés ; la rendre
 * multipliable n'a demandé que de lire son instance. Un événement qui veut recueillir
 * des avis ajoute une page formulaire et l'appelle « Satisfaction ».
 *
 * ⚠️ JAMAIS DANS LE TUNNEL, et c'est ce qui la distingue de l'étape « informations » :
 * on ne pose pas de questions à quelqu'un qui n'est pas encore inscrit. Même
 * machinerie de formulaire du back office (`useChosenForm`, `saveAnswers`, `FieldRow`,
 * `check`), le même enregistrement sur place, mais aucune logique de parcours.
 *
 * ⚠️ ON NE FACTORISE TOUJOURS PAS AVEC `Informations`. Les deux écrans se ressemblent,
 * mais Informations est déployé, critique (il écrit l'identité qui imprime le badge) et
 * porte la logique du tunnel (instances de parcours, fil, refus d'e-mail) dont cette
 * page n'a rien. On réutilise les BRIQUES déjà extraites, pas le fichier.
 *
 * ⚠️ CE QUE VOIT QUELQU'UN QUI A DÉJÀ RÉPONDU : ses réponses, modifiables. Le
 * formulaire s'ouvre sur ce que le serveur détient et se renvoie — cadré par l'user le
 * 2026-08-20. Une page qui se fermerait après coup demanderait un réglage de plus et
 * un texte de plus à traduire, pour retirer un geste que les gens attendent.
 *
 * Displayed strings are French: this is a French product.
 */
export function FormPage() {
  const t = useT();
  const event = useEvent();
  const client = useQueryClient();
  const navigate = useNavigate();
  const { me } = useParticipation();
  const sessionKey = useSessionKey();

  /* `/form` → pas d'instance, clés nues ; `/form/2` → clés suffixées `_2`. */
  const instance = useParams().instance;
  const page = pageOfInstance(FORM_PAGE, instance);

  /* ⚠️ LE TITRE EST LE NOM DE LA PAGE, pas un réglage à lui. C'est la règle de la
     section : deux mots pour une seule chose, et renommer la page dans le
     gestionnaire aurait laissé son en-tête dire l'ancien. */
  const { name, subtitle } = useSitePages();
  const title = name(page);
  const sub = subtitle(page);
  const html = useText(htmlKey(FORM_PAGE, instance));

  /* Le formulaire que sert cette page, et ses libellés dans la langue lue — les mêmes
     deux lectures que les autres écrans qui servent un formulaire du back office. */
  const form = useChosenForm(formIdKey(FORM_PAGE, instance));
  const words = useWords(form);

  /** Ce que cette personne a déjà répondu — le formulaire s'ouvre dessus. */
  const stored = useQuery({
    queryKey: ["form-answers", event?.domainId, event?.eventId, me?.id],
    queryFn: ({ signal }) =>
      fetchAnswers(
        event!.domainId,
        { eventId: event!.eventId, userId: me!.id },
        signal,
      ),
    enabled: !!event && typeof me?.id === "number",
    staleTime: Infinity,
    retry: false,
  });

  /* Ce qui a été SAISI, par-dessus ce que le serveur détient. Dérivé et non initialisé :
     les réponses arrivent de façon asynchrone, et un état initialisé une seule fois
     laisserait le formulaire vide au-dessus de réponses qui existent. */
  const [typed, setTyped] = useState<Record<string, unknown>>({});
  const fields = form?.fields ?? [];
  const values = Object.fromEntries(
    fields.map((f) => [
      f.id,
      f.id in typed ? typed[f.id] : valueOf(stored.data ?? {}, f),
    ]),
  );

  const locked = lockedOf(fields, stored.data ?? {});

  /**
   * Y A-T-IL QUELQUE CHOSE À REMPLIR ? Même règle que l'étape informations, et pour la
   * même raison : un formulaire peut EXISTER sans porter une seule question servable
   * (lignes sans composant écartées par `fieldOf`, formulaires de domaine de la V1 sans
   * aucune ligne). La page rendait alors un `<form>` vide entre le titre et un « Envoyer »
   * grisé, sans un mot — l'alerte, elle, ne parlait que du formulaire absent.
   */
  const servable = !!form && !!words && fields.length > 0;
  const organiser = useMode() === "organiser";

  const [saving, setSaving] = useState(false);
  const [failed, setFailed] = useState(false);
  const [saved, setSaved] = useState(false);
  const [complaints, setComplaints] = useState<Complaint[]>([]);

  /** Chaque reproche sous son champ, le focus sur le premier. */
  const show = (rs: Complaint[]) => {
    setComplaints(rs);
    setFailed(false);
    if (rs[0]) document.getElementById(`f-${rs[0].id}`)?.focus();
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (saving || !event || typeof me?.id !== "number") return;
    // ⚠️ Session changée depuis le rendu (autre onglet) : ce qui est ici est à l'ancien compte, rien ne part.
    if (!sameSession(sessionKey)) return;
    setComplaints([]);
    setFailed(false);
    setSaved(false);

    /**
     * Tous les fautifs d'un coup, le focus sur le premier : voir `allInvalid`.
     *
     * ⚠️ PAR `allInvalid`, ET NON SUR `fields` : cet écran jugeait TOUS les champs, y
     * compris ceux que leur condition replie. Un champ obligatoire caché bloquait alors
     * « Envoyer » POUR TOUJOURS, et en silence — la liste rendue est `visibleFields`, donc
     * le reproche ne trouvait aucune rangée où s'écrire, et `show` focalisait un élément
     * absent du document. La seule issue était de rouvrir le champ et de le remplir.
     * `allInvalid` porte la règle (« un champ caché ne se reproche pas ») et c'est déjà
     * ce que font l'étape informations, la société et les accompagnants.
     */
    const invalid = allInvalid(fields, (f) => values[f.id], locked);
    if (invalid.length > 0) {
      show(invalid.map((i) => i.complaint));
      return;
    }

    setSaving(true);
    try {
      await saveAnswers({
        domainId: event.domainId,
        target: { eventId: event.eventId, userId: me.id },
        fields,
        values,
        current: stored.data ?? {},
        locked,
      });

      /* Notre propre écriture vient de périmer le cache : on marque, sans relire tout de
         suite (l'écran reste, le mot de remerciement suffit). Même clé que les autres
         écrans à formulaire, et la fiche de la personne au cas où ce formulaire toucherait
         un champ du modèle `U`. */
      await client.invalidateQueries({
        queryKey: ["form-answers", event.domainId, event.eventId, me.id],
        refetchType: "none",
      });
      await client.invalidateQueries({
        queryKey: ["participant"],
        refetchType: "none",
      });

      setSaved(true);
    } catch (error) {
      /* Une page formulaire porte rarement l'e-mail, mais si elle le fait, un refus se
         dit sous le champ visé plutôt qu'en bandeau générique — même règle
         qu'Informations. Le seul champ que le serveur examine est le `U.email`. */
      const field = fields.find(
        (f) => isEmail(f) && f.model === "U" && !locked.has(f.id),
      );
      const message = field ? emailRefusal(error) : null;
      if (field && message) show([{ id: field.id, message }]);
      else setFailed(true);
    } finally {
      setSaving(false);
    }
  };

  return (
    /* `data-page` : l'exemplaire, qui borne une règle d'apparence à CETTE page —
       voir `selectorOf`. */
    <section className="page" data-page={page.key}>
      <div className="ttl" data-cfg="formTitle">
        <h2>{title}</h2>
        {sub.trim() !== "" && <p>{sub}</p>}
      </div>

      {html.trim() !== "" && (
        <Prose
          html={html}
          className="se-prose"
          data-cfg="formTitle"
          />
      )}

      {/* ⚠️ AUCUN FORMULAIRE CHOISI, UN QUI N'EXISTE PLUS, OU UN SANS AUCUNE QUESTION. Dit
          à l'organisateur, seul à pouvoir le corriger, plutôt qu'une page vide où l'on
          cherche quoi remplir. ⚠️ À LUI SEUL : le participant n'a ni formulaire à
          choisir ni menu à régler — il garde le titre et le retour à l'accueil. */}
      <div className="alert" data-cfg="formForm" hidden={servable || !organiser}>
        <span className="alert__d" aria-hidden="true">
          <i />
        </span>
        <span>
          <b>{form ? "Formulaire sans question" : "Aucun formulaire"}</b>
          <small>
            {form
              ? "Le formulaire choisi n'a aucune question servable : choisissez-en un autre, ou retirez la page du menu."
              : "Choisissez le formulaire que cette page doit servir, ou retirez-la du menu."}
          </small>
        </span>
      </div>

      {servable && (
        <form
          className="form"
          data-cfg="formForm"
          /* Le reproche est celui de l'écran, pas la bulle du navigateur : `check` juge
             tous les champs, groupes de choix compris. Voir `Informations`. */
          noValidate
          onSubmit={submit}
        >
          {visibleFields(fields, (f) => values[f.id]).map((field) => (
            <FieldRow
              key={field.id}
              field={field}
              value={values[field.id]}
              locked={locked.has(field.id)}
              words={words}
              error={complaints.find((r) => r.id === field.id)?.message}
              onChange={(v) => {
                setTyped((t) => ({ ...t, [field.id]: v }));
                setComplaints((rs) => rs.filter((r) => r.id !== field.id));
              }}
            />
          ))}

          {failed && (
            <p className="f--wide loginerr" role="alert">
              {t("ANSWER_NOT_SAVED_RETRY", "Votre réponse n'a pas pu être enregistrée. Réessayez.")}
            </p>
          )}

          {/* Servie en page, on ne change pas d'écran : ce mot est la seule preuve que le
              bouton a servi. C'est la phrase de la V1. */}
          {saved && (
            <p className="f--wide step__ok" role="status">
              {t("ANSWER_RECORDED", 'Votre réponse a bien été prise en compte.')}
            </p>
          )}

          <div className="actions f--wide">
            {/* Hors du tunnel, rien ne ramène : sur un téléphone, le menu est en bas et
                derrière un onglet. Même bouton, même place que le badge et Informations. */}
            <Button
              type="button"
              variant="ghost"
              onClick={() => navigate(withContext("/", event?.token))}
            >
              {t("BACK_TO_HOME", "Retour à l'accueil")}
            </Button>
            <Button type="submit" data-cfg="formForm" loading={saving}>
              {t("SEND", "Envoyer")}
            </Button>
          </div>
        </form>
      )}

      {/* ⚠️ RIEN À REMPLIR NE DOIT PAS RETENIR : le bouton de retour vivait dans le même
          test que le formulaire et disparaissait avec lui, ne laissant que le menu — en
          bas de l'écran et derrière un onglet, sur un téléphone. */}
      {!servable && (
        <div className="actions">
          <Button
            type="button"
            variant="ghost"
            onClick={() => navigate(withContext("/", event?.token))}
          >
            {t("BACK_TO_HOME", "Retour à l'accueil")}
          </Button>
        </div>
      )}
    </section>
  );
}
